import { type ReactNode, useId } from "react";

// One choice of a few, each a radio with its name and what it does under it
// (byj-1wv.51.18, byj-1wv.93): where a select would cut a long option short
// or hide what each means — the bank's catalog setting, a review's three
// verdicts, a file storage setting as deployed or an admin's own. An option
// may carry more after its words (a value of one's own typed in its field).

export type ChoiceOption<T extends string> = { value: T; name: ReactNode; what?: ReactNode; extra?: ReactNode };

export function Choices<T extends string>({ name, legend, legendShown = true, options, value, onChange, className, testid }: {
  /** the radios' group name, unique on the page */
  name: string;
  legend: ReactNode;
  /** false where a heading beside the choice says it already (a settings row): read aloud, not shown twice */
  legendShown?: boolean;
  options: ChoiceOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** a place's own look beside `setting-choice` (the bank's `bank-choice`) */
  className?: string;
  testid?: string;
}) {
  const id = useId();
  return (
    <fieldset className={`setting-choice${className ? ` ${className}` : ""}`} data-testid={testid}>
      <legend className={legendShown ? "small" : "sr-only"}>{legend}</legend>
      {options.map((o, i) => (
        <label key={o.value} className={`setting-option${value === o.value ? " is-chosen" : ""}`}>
          {/* named by its name alone and described by what it does: never by what is typed in its extra, which would rename it at each key */}
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            aria-labelledby={`${id}-${i}-name`}
            aria-describedby={o.what ? `${id}-${i}-what` : undefined}
          />
          <span className="stack tight">
            <strong id={`${id}-${i}-name`}>{o.name}</strong>
            {o.what && (
              <span className="muted" id={`${id}-${i}-what`}>
                {o.what}
              </span>
            )}
          </span>
          {o.extra}
        </label>
      ))}
    </fieldset>
  );
}
